import {createImmutableProxy, deepClone} from '@wix/wix-immutable-proxy'
import type {ConversionDal, ValueTransformer} from '../types'
import type {Pointer} from '@wix/document-services-types'
import {
    createList,
    createTagManager,
    type DalJsStore,
    deepCompare,
    type SnapshotDal,
    createFragmentStore,
    FragmentStore,
    createFragmentStoreFromJS,
    PAGE_FRAGMENT_TYPE,
    pointerUtils
} from '@wix/document-manager-core'
import type {ConversionDalEntryItem} from './types'
import _ from 'lodash'
import {validators} from './validators'

export const createConversionDal = (initialStore: DalJsStore): ConversionDal => {
    const store = createFragmentStore()
    const snapshots = createList()
    const tagManager = createTagManager()
    let openSnapshotStore = createFragmentStore()
    const _validate = _.over(validators)

    const getFromStores = (stores: FragmentStore[], pointer: Pointer): ConversionDalEntryItem => {
        const foundStore = stores.find((s: FragmentStore) => s.has(pointer))
        return foundStore ? foundStore.get(pointer) : undefined
    }

    const isChanged = (pointer: Pointer, value: ConversionDalEntryItem) => {
        const currentValue = getFromStores([openSnapshotStore, store], pointer)

        return !deepCompare(currentValue, value)
    }

    const _set = (pointer: Pointer, value: ConversionDalEntryItem): void => {
        openSnapshotStore.set(pointer, deepClone(value))
    }

    const setIfChanged = (pointer: Pointer, value: ConversionDalEntryItem): void => {
        if (isChanged(pointer, value)) {
            _set(pointer, value)
        }
    }

    const set = (pointer: Pointer, value: ConversionDalEntryItem): void => {
        setIfChanged(pointer, value)
    }

    const getRawValue = (pointer: Pointer): ConversionDalEntryItem => {
        return getFromStores([openSnapshotStore, store], pointer)
    }

    const get = (pointer: Pointer): ConversionDalEntryItem => {
        return createImmutableProxy(getRawValue(pointer))
    }

    const modify = (pointer: Pointer, transformer: ValueTransformer): void => {
        return set(pointer, transformer(deepClone(get(pointer))))
    }

    const validate = (): void => {
        openSnapshotStore.forEach((fragmentStore, fragmentType) => {
            fragmentStore.forEach((pointer: Pointer) => {
                _validate(store, openSnapshotStore, pointerUtils.getPointer(pointer.id, pointer.type, {fragmentType}))
            })
        })
    }

    const commitChanges = (tag: string): SnapshotDal => {
        if (openSnapshotStore.isEmpty()) {
            return snapshots.last()!
        }

        validate()
        store.merge(openSnapshotStore)
        const snapshot = snapshots.add(openSnapshotStore, snapshots.generateId(tag))
        tagManager.addSnapshot(tag, snapshot)
        openSnapshotStore = createFragmentStore()

        return snapshot
    }

    const mergeToStore = (changes: FragmentStore, label: string) => {
        const clonedChanges = changes.clone()
        const snapshotId = snapshots.generateId(label)

        store.merge(clonedChanges)
        snapshots.add(clonedChanges, snapshotId)
    }

    const getLastSnapshot = () => snapshots.last()

    if (initialStore) {
        mergeToStore(createFragmentStoreFromJS({[PAGE_FRAGMENT_TYPE]: initialStore}), 'initial store')
    }

    return {
        set,
        modify,
        get,
        commitChanges,
        getLastSnapshot
    }
}
